// boot() — the single orchestrator entry for @raventic/sdk.
//
// Implements PLAN.md flow 1–7. Each step is wrapped in try/catch so one step's failure
// never aborts the others; errors are collected into the BootResult and console.error'd
// (a facade-level error handler lands later).
//
// Order of steps. Everything up to the DOM wait runs synchronously at widget.js execution
// (the embed is `<script async>`, possibly while the parser is still in <head>) — none of
// it reads the host page (iteration 20, "head start"):
//   light stylesheet → staged config / experiment arm → <html> attributes → A/B exposure event → early-click
//   guards (dropdown, search modal, assistant modal) → runtime (SDKs' configure()) →
//   script-start activation context → search prepared (results' `hide` validated + predicate
//   evaluated once, the click widgets' + a drawing results' `hide` injected into <head>, a
//   drawing results' search request started — `ResultsWidget.prefetch`, iteration 24) →
//   shadow stylesheet preload (drawing results) / prefetch (the rest) → preconnect (origins a
//   drawing widget hits) → analytics (started; identification waits for DOM readiness) →
//   building the widget pipelines
//   → one pipeline per widget, each: (await its readiness — today the shared DOM readiness,
//     after which the guards are re-armed, the A/B event re-checked and the page activation
//     context built, once) → release its
//     guard → create / draw → replay early clicks. Started dropdown → search modal → assistant
//     modal → results → each carousel; none awaits another; boot() settles when all have
//     (iteration 23).
// `BootResult.errors` follows the order failures happen in: analytics / runtime / A/B errors
// precede every DOM-dependent one; among the widgets, sync failures come in start order and
// results' async create() failures whenever they settle.
//
// Config experiments (iteration 18 config experiments, ./ab.ts): when the config is an
// ExperimentConfig, the arm is resolved ONCE here, at the top (resolveArm: URL override →
// sticky `_rvn_ab:<id>` → weighted pick; synchronous, storage + URL only, so it needs no DOM),
// and everything after it runs on ONE plain config, `{ ...arm, analytics }` (a null arm →
// `{ analytics }`: no widgets, analytics only). So no later step knows about arms: the guards,
// `data-rvn-active`, the `hide` <style>, the results prefetch, the stylesheet hints /
// preconnects and the widget pipelines simply follow the booted config — a null arm gets none
// of them. Analytics runs on every arm, carrying the variant. The exposure event is pushed
// right away too (and re-pushed at DOM readiness if a host script replaced its layer in
// between — ./ab.ts keepAbEvent). A broken experiment config → boot error "ab", arm 0,
// unrecorded.
//
// Staged configs (iteration 27, ./staged.ts): when the config carries `staged` and the page URL
// or the tab's sessionStorage switches one on, that staged config (+ the shared analytics) is
// what runs — resolved first, in the same step, and it REPLACES the live config, an experiment
// included: no arm is resolved (`_rvn_ab:<id>` untouched), no exposure event, no variant, no
// `data-rvn-ab`; `<html data-rvn-staged="<name>">` instead. At DOM readiness the staged badge is
// shown, with a button back to live. All of it is the staged RUNTIME defineStaged attaches
// (./staged-runtime.ts) — boot() only calls `input.staged?.runtime?.select?.(…)`, so a bundle
// without staged configs carries none of that code. A broken staged block → boot error
// "staged", the live config runs.
//
// Early-click guards (iteration 14 for the dropdown, iteration 22 for every click widget):
// installed before the DOM wait so an activation of a click widget's element (the shop's
// search box, a search icon, an "ask the assistant" link) before that widget exists is
// swallowed and replayed as open() once it does (./click-guard.ts). One independent guard per
// widget; each self-release deadline is re-armed at DOM readiness. Each is released by its
// product step right before that widget's create(), and again — idempotently — in a finally
// here, so no path (a product failing, boot itself throwing) leaves one armed.
//
// Late activation (iteration 13): results / carousels whose `activateWhen` is async
// or a trigger are started here but NOT awaited — boot() settles as before, and
// their later outcomes go to `config.onActivation` (./activation-runtime.ts), not
// BootResult. On a null arm nothing is subscribed, so nothing can draw late.

import type { ArmConfig, CustomerConfig, ExperimentConfig, RaventicSdkConfig } from "./config";
import { applyRuntime, resolveRuntime } from "./runtime";
import { injectLightStylesheet } from "./light-styles";
import { whenDomReady } from "./dom-ready";
import { installAssistantGuard, installDropdownGuard, installSearchModalGuard } from "./click-guard";
import type { AssistantGuard, IndexGuard } from "./click-guard";
import { keepAbEvent, markArm, pushAbEvent, resolveArm } from "./ab";
import type { AbPush } from "./ab";
import { buildContext } from "./activation";
import type { ActivationContext } from "./activation";
import { createReporter } from "./activation-runtime";
import { originsToPreconnect, preconnect, stylesheetsToWarm, warmStylesheets } from "./warmup";
import { initAnalytics } from "./products/analytics";
import { prepareSearch, searchPipelines } from "./products/search";
import type { SearchBootPlan, SearchInitResult, SearchSteps } from "./products/search";
import { assistantPipeline } from "./products/assistant";
import type { AssistantInitResult } from "./products/assistant";
import { rcePipelines } from "./products/rce";
import type { RceInitResult } from "./products/rce";

// When a widget may start its pipeline (see `readiness` in bootSteps — iteration 25's seam).
export type Readiness = () => Promise<void>;

export interface BootResult {
  errors: Array<{ product: string; error: unknown }>;
  search?: SearchInitResult;
  assistant?: AssistantInitResult;
  rce?: RceInitResult;
  // The experiment arm this load landed in ("0", "1", … = the arm index); null for a plain
  // config, and for a broken experiment (arm 0 ran, unrecorded).
  variant: string | null;
  // The experiment's id whenever `variant` is set; null otherwise.
  experiment: string | null;
  // The staged config this load runs (iteration 27); null = the live config. When set,
  // `variant` / `experiment` are null: a staged config replaces the experiment.
  staged: string | null;
}

export async function boot(config?: CustomerConfig): Promise<BootResult> {
  // If no config is passed, read the global the customer thin-entry installs.
  const input = config ?? window.RaventicSDK;
  const result: BootResult = { errors: [], variant: null, experiment: null, staged: null };

  // Nothing to boot: neither an argument nor window.RaventicSDK.
  if (!input) return result;

  // Records a failed step (never rethrows: no step may abort boot()).
  const fail = (product: string, message: string, error: unknown): void => {
    result.errors.push({ product, error });
    console.error(`[@raventic/sdk] ${message}`, error);
  };

  // 0. Link the customer's light-DOM stylesheet (widget.css) into <head> — the bundle is
  //    the only thing the customer embeds, so it loads its own stylesheet. First, so the
  //    fetch starts as early as possible. Runs on EVERY experiment arm — it is linked before
  //    the arm is resolved and not gated by it (one stylesheet per bundle) — so the light
  //    CSS must be arm-neutral: rules for widget-rendered markup (or correct with and
  //    without each arm's widgets) are fine anywhere, but a rule that hides / alters a host
  //    element (the native search box, the shop's own recommendations) must be scoped under
  //    `[data-rvn-active]` (step 2: any arm with widgets — never set on a null arm; so only for
  //    host elements EVERY widget-bearing arm replaces) or under one arm's `[data-rvn-ab="<n>"]`
  //    (optionally with `[data-rvn-experiment="<id>"]`, the experiment marker set on every
  //    recorded arm) — unscoped, it would corrupt a null / baseline arm (documented for customers in styles/light.scss.example +
  //    README). The URL is baked at build time ("" = the build produced no widget.css →
  //    no-op). Guarded: a stylesheet failure must never block the widgets.
  try {
    injectLightStylesheet(process.env.RAVENTIC_LIGHT_CSS_URL);
  } catch (error) {
    fail("styles", "light stylesheet injection failed", error);
  }

  // 1. Staged config, else experiment arm: resolved ONCE for the whole page, before anything
  //    that follows the booted config. Synchronous and page-independent (storage + URL), so it
  //    needs no DOM. A staged config switched on replaces the live config — an experiment is
  //    then not resolved at all. Never throws: the staged runtime / isExperiment tolerate a
  //    non-object `window.RaventicSDK`, the runtime reports a broken staged block (→ live) and
  //    selectArm a broken experiment. From here on `cfg` is one plain config: the staged
  //    config / the arm + the shared analytics.
  //    The staged part is all defineStaged's runtime (./staged-runtime.ts), so a config without
  //    staged configs pays only this optional chain. `.staged` on a non-object
  //    `window.RaventicSDK` is just undefined; a hand-written `staged` map (no defineStaged: a
  //    type error) has no runtime and is ignored.
  const staged = (input as CustomerConfig).staged?.runtime?.select?.(input, result, fail);
  const cfg: RaventicSdkConfig = staged?.config ?? (isExperiment(input) ? selectArm(input, result) : input);
  // Whether the booted config has any widget (a null arm: none). `data-rvn-active` and the
  // warm-up follow it; every per-widget step checks its own section.
  const active = hasWidgets(cfg);

  // 2. The arm on <html> (./ab.ts markArm): `data-rvn-ab="<n>"` + `data-rvn-experiment="<id>"`
  //    whenever an experiment arm was recorded, `data-rvn-active` whenever the booted config
  //    has widgets (never on a null arm). `document.documentElement` exists whenever a script
  //    runs. Guarded (product "ab").
  try {
    markArm(result.variant, result.experiment, active);
  } catch (error) {
    fail("ab", "A/B <html> attributes failed", error);
  }
  // The running staged config: `data-rvn-staged` on <html> now, the badge at DOM readiness (it
  // needs <body>) — on its own, the widgets don't wait for it. Guarded: a failure costs only the
  // mark / badge (the staged config still runs; `?rvn_staged=off` still leaves it).
  const stagedShown = staged?.show();

  // 3. A/B exposure event for EVERY arm, the null arm included — needs no DOM, so pushed at
  //    once. Not for a plain config, nor for a broken experiment (arm 0 runs unrecorded). A
  //    failed push leaves the resolved variant standing (analytics' init event carries it).
  //    Its entries are re-checked at DOM readiness.
  let abPush: AbPush | undefined;
  if (result.variant !== null && result.experiment !== null) {
    try {
      abPush = pushAbEvent(result.variant, result.experiment, cfg.analytics);
    } catch (error) {
      fail("ab", "A/B exposure event failed", error);
    }
    // Misconfig, not an error: the arm still runs, but only dataLayer (GTM) sees it —
    // Raventic records nothing.
    if (!cfg.analytics) {
      console.warn(
        `[@raventic/sdk] experiment "${result.experiment}" runs without analytics — the variant will not be recorded`,
      );
    }
  }

  // 4. Early-click guards: swallow + remember activations of each click widget's elements
  //    from now until that widget exists (./click-guard.ts). Before the DOM wait on purpose —
  //    the elements may not be parsed yet, which the delegated document listeners don't need.
  //    Only for the click widgets the booted config has: an arm without one — a null arm —
  //    leaves the shop's own elements untouched. Each guarded on its own: a failing guard only
  //    costs that widget's early clicks.
  //    - dropdown: the widget treats an <input> as its external input only outside chatbot
  //      mode, and so does the guard;
  //    - search modal: the dropdown's non-input binding;
  //    - assistant modal: only with `chatbot.enabled` (else the widget binds nothing and a
  //      trigger link must keep working); null = nothing to guard (invalid triggers).
  const guards: EarlyGuards = {};
  const dropdown = cfg.search?.dropdown;
  if (dropdown) {
    try {
      guards.dropdown = installDropdownGuard(dropdown.activateOn, {
        inputIsExternal: !dropdown.config?.chatbot?.enabled,
      });
    } catch (error) {
      fail("search:dropdown-guard", "early dropdown guard failed", error);
    }
  }
  const modal = cfg.search?.modal;
  if (modal) {
    try {
      guards.modal = installSearchModalGuard(modal.activateOn);
    } catch (error) {
      fail("search:modal-guard", "early search modal guard failed", error);
    }
  }
  const assistant = cfg.assistant?.modal;
  if (assistant) {
    try {
      if (assistant.config?.chatbot?.enabled === true) {
        guards.assistant = installAssistantGuard(assistant.triggers) ?? undefined;
      }
    } catch (error) {
      fail("assistant:modal-guard", "early assistant modal guard failed", error);
    }
  }

  try {
    await bootSteps(cfg, result, active, abPush, guards, fail);
  } finally {
    // Normally each was already released by its product step (before create()); this covers
    // every other path — a product failing early, a throw anywhere in bootSteps. Idempotent.
    guards.dropdown?.release();
    guards.modal?.release();
    guards.assistant?.release();
  }
  await stagedShown;
  return result;
}

// An experiment (defineExperiment) vs a plain config: only an experiment has `arms`. The
// object check first: `window.RaventicSDK` is set by page code, and `"arms" in true` would
// throw; a non-object stays a "plain config" (whose sections are all absent), as before.
function isExperiment(config: CustomerConfig): config is ExperimentConfig {
  return typeof config === "object" && config !== null && "arms" in config;
}

// Resolve the experiment's arm for this load (./ab.ts) and return the plain config boot()
// runs: the arm's own config + the experiment's shared analytics (`{ analytics }` alone for
// a null arm). Records the arm in `result`. A broken experiment config (resolveArm throws)
// → boot error "ab" + console.error, and arm 0 runs UNRECORDED (variant / experiment stay
// null: no exposure event, no `data-rvn-ab`, and analytics' init event carries no variant) —
// arm 0 is the baseline, the safest fallback. Never throws.
function selectArm(experiment: ExperimentConfig, result: BootResult): RaventicSdkConfig {
  let arm: ArmConfig | null | undefined;
  try {
    const resolved = resolveArm(experiment);
    arm = experiment.arms[resolved.index];
    result.variant = resolved.variant;
    result.experiment = experiment.id;
  } catch (error) {
    result.errors.push({ product: "ab", error });
    console.error("[@raventic/sdk] experiment misconfigured — running arm 0, unrecorded", error);
    arm = Array.isArray(experiment.arms) ? experiment.arms[0] : undefined;
  }
  // The experiment's `analytics` ALWAYS wins: ArmConfig forbids one in an arm, but a
  // plain-JS config could still carry it. Set only when present, so absent stays absent.
  const cfg: RaventicSdkConfig = { ...arm };
  delete cfg.analytics;
  if (experiment.analytics !== undefined) cfg.analytics = experiment.analytics;
  return cfg;
}

// The booted config has at least one actual widget: a search dropdown / modal / results, an
// assistant modal, or a non-empty `rce.carousels`. An empty section (`search: {}`,
// `assistant: {}`, `rce: { carousels: [] }`) doesn't count. False for a null arm
// (`{ analytics }` only) and for an analytics-only plain config. Exported for tests.
export function hasWidgets(cfg: RaventicSdkConfig): boolean {
  const search = cfg.search;
  const carousels = cfg.rce?.carousels;
  return Boolean(
    search?.dropdown ||
      search?.modal ||
      search?.results ||
      cfg.assistant?.modal ||
      (Array.isArray(carousels) && carousels.length > 0),
  );
}

interface EarlyGuards {
  dropdown?: IndexGuard;
  modal?: IndexGuard;
  assistant?: AssistantGuard;
}

type Fail = (product: string, message: string, error: unknown) => void;

// The rest of boot(): the remaining script-start steps, the DOM wait, then the page-reading
// steps. Fills `result` in place.
async function bootSteps(
  cfg: RaventicSdkConfig,
  result: BootResult,
  active: boolean,
  abPush: AbPush | undefined,
  guards: EarlyGuards,
  fail: Fail,
): Promise<void> {
  // 5. Apply runtime URLs once (both SDKs' configure()). Their configure() only merges
  //    values into module state — no DOM — so it runs at script start: everything that
  //    later reads those settings (the results prefetch in step 7, the warm-up) sees them.
  //    Guarded like every step so a throwing configure() can't abort the rest of boot()
  //    (notably the always-on analytics guarantee).
  try {
    applyRuntime(cfg.runtime);
  } catch (error) {
    fail("runtime", "runtime configure failed", error);
  }

  // 6. The SCRIPT-START activation context: analytics' predicate and results' verdict (step 7)
  //    are evaluated with it. buildContext() reads only `location` and the `document` reference,
  //    so it is fine before parsing. The widget pipelines get a fresh PAGE context built at DOM
  //    readiness instead (a <head> script's history.replaceState is reflected there). A failure
  //    (practically unreachable: location.href always parses) skips only what needs it.
  let startCtx: ActivationContext | undefined;
  try {
    startCtx = buildContext();
  } catch (error) {
    fail("context", "activation context failed", error);
  }

  // 7. Search, prepared at script start (iteration 21, products/search.ts prepareSearch), when
  //    the booted config has a search section (a null arm has none): results' `hide` is validated and its `activateWhen` evaluated ONCE here
  //    (a sync `false` while loading is re-checked once after the wait; async / trigger keep the
  //    late path), and the `hide` rules of the dropdown, the modal and a results widget that will
  //    draw are injected as one `<style data-rvn-hide>` now — before the parser reaches the
  //    native elements they hide. The plan goes to the search steps (per-widget errors are
  //    recorded there, once, in each widget's own try/catch). When results will draw, its search
  //    request starts here too (iteration 24: `ResultsWidget.prefetch`, after step 5 configured
  //    the search API URL), and the handle rides the plan into results' create().
  let searchPlan: SearchBootPlan | undefined;
  if (cfg.search && startCtx) {
    try {
      searchPlan = prepareSearch(cfg.search, startCtx);
      // The <style> couldn't be injected: every widget fell back to customPageStyles.
      if (searchPlan.hideError !== undefined) fail("search:hide", "search hide injection failed", searchPlan.hideError);
    } catch (error) {
      fail("search", "search preparation failed", error);
    }
  }
  const drawsNow = { results: searchPlan?.results?.kind === "draw" };

  // 8–9. Network warm-up (./warmup.ts), only when the booted config has widgets (a null arm has
  //    none to warm):
  //    `preload` the shadow stylesheet a widget that renders right after DOM readiness will link
  //    (results, when step 7 says it draws), `prefetch` the ones the other widgets may link later
  //    (on open / on a draw), and preconnect to the API / image origins a drawing widget hits
  //    first. Both only append <link>s to <head>.
  if (active) {
    try {
      warmStylesheets(stylesheetsToWarm(cfg, resolveRuntime(cfg.runtime), drawsNow));
    } catch (error) {
      fail("styles:preload", "shadow stylesheet warm-up failed", error);
    }
    try {
      preconnect(originsToPreconnect(cfg, resolveRuntime(cfg.runtime), window.location.origin, drawsNow));
    } catch (error) {
      fail("preconnect", "preconnect failed", error);
    }
  }

  // The DOM readiness promise — created once, here, because analytics (step 10) needs it too.
  const parsed = whenDomReady();

  // 10. Analytics — on by default on every page unless explicitly disabled. Started at
  //    once (products/analytics.ts TIMING): its unload-flush listeners only exist after
  //    start(), and a visitor may leave before the DOM is parsed — but its identification
  //    (consent read, `rvn_c` / `rvn_p` storage) waits for DOM readiness (`parsed`), when the
  //    page's consent signals exist. Runs on EVERY experiment arm (the shared `analytics` — it
  //    has to measure the null / baseline arm too) with `result.variant`. `analytics === false` disables it; an AnalyticsActivation object
  //    enables it. `analytics === undefined` cannot init (no apiKey) — a no-op. Its
  //    `activateWhen` runs now, before parsing (with the script-start context, built here inside
  //    the guard if step 6 failed).
  if (cfg.analytics) {
    try {
      initAnalytics(cfg.analytics, startCtx ?? buildContext(), result.variant, parsed);
    } catch (error) {
      fail("analytics", "analytics init failed", error);
    }
  }

  // One reporter for every late activation (wraps the customer's onActivation). Guarded: a
  // throwing `onActivation` getter is recorded and the reporter works without the customer's
  // handler.
  let report: ReturnType<typeof createReporter>;
  try {
    report = createReporter(cfg.onActivation);
  } catch (error) {
    fail("onActivation", "onActivation handler unavailable", error);
    report = createReporter();
  }

  // The DOM wait (iteration 23: shared, but awaited by each widget's pipeline on its own).
  // Wait for the host document to be parsed before anything that reads the page. The embed is
  // `<script async>`, which may execute while the parser is still in <head> — before the
  // search box / results container / carousel slots exist — and every widget step evaluates
  // selectors / activation predicates at call time. boot() was already async, so this changes
  // no API: the returned promise settles once every pipeline has, with the same fully filled
  // BootResult (no partial result is ever handed out). When the document is already parsed
  // (the usual case, and always in the dev harness) this costs a microtask. The page-level
  // after-readiness work runs first, once:
  //   - the guards' deadlines restart (≤ CLICK_GUARD_TIMEOUT_MS after readiness), so a slow
  //     DOMContentLoaded can't use them up before the widgets are even created;
  //   - 11. the A/B exposure event pushed at step 3 must still be in its layer(s): a host
  //     script parsed after widget.js may have replaced `window.dataLayer` /
  //     `window.raventicLayer` (./ab.ts keepAbEvent);
  //   - the PAGE activation context is built (see step 6) for the widget steps.
  let pageCtx: ActivationContext | undefined;
  const domReady = parsed.then(() => {
    guards.dropdown?.rearm();
    guards.modal?.rearm();
    guards.assistant?.rearm();
    if (abPush) {
      try {
        keepAbEvent(abPush);
      } catch (error) {
        fail("ab", "A/B event re-check failed", error);
      }
    }
    try {
      pageCtx = buildContext();
    } catch (error) {
      fail("context", "activation context failed", error);
    }
  });
  // The widget steps read it when they run (after readiness); each step's own try/catch records
  // a missing one as its error.
  const ctx = (): ActivationContext => {
    if (!pageCtx) throw new Error("[@raventic/sdk] no activation context");
    return pageCtx;
  };
  // THE READINESS SEAM (iteration 25's hook): when a widget may start. Today every widget waits
  // for the same DOM readiness; iteration 25 will hand a draw widget opted into early mounting
  // a "its element has appeared (and is closed)" promise instead, without touching the others.
  const readiness = (_widget: string): Readiness => () => domReady;

  // 12–14. One independent pipeline per widget (iteration 23): `await ready()` → (its step:)
  //    release its early-click guard → create / draw → replay early clicks / report. No
  //    pipeline waits for another — a slow results render no longer delays the assistant or the
  //    carousels — and boot() settles when all of them have (Promise.allSettled). Started in
  //    this order (all resume in it once ready): click widgets first (sync, cheap creates),
  //    then results, then the carousels. Each step isolates its own failure (BootResult product
  //    `search:<widget>` / `assistant:modal` / `rce[i:type]`); building a section's steps is
  //    guarded too (a malformed section: `search` / `assistant` / `rce`). Late activations
  //    (async predicates / triggers) stay outside boot(), as before (onActivation). Only the
  //    booted config's own widgets get one (none on an experiment's null arm).
  const pipelines: Array<Promise<void>> = [];
  const start = (widget: string, product: string, step: (() => void | Promise<void>) | undefined): void => {
    if (!step) return;
    const ready = readiness(widget);
    pipelines.push(
      (async () => {
        await ready();
        await step();
      })().catch((error: unknown) => fail(product, `${widget} pipeline failed`, error)),
    );
  };

  let search: SearchSteps = {};
  if (cfg.search) {
    try {
      const s: SearchInitResult = { errors: [] };
      result.search = s;
      search = searchPipelines(cfg.search, ctx, report, guards, searchPlan ?? {}, s, (widget, error) =>
        result.errors.push({ product: `search:${widget}`, error }),
      );
    } catch (error) {
      fail("search", "search init failed", error);
    }
  }

  // The assistant's step configures the assistant's own runtime with the same resolved URLs
  // as step 5, then creates the widget.
  let assistant: (() => void) | undefined;
  if (cfg.assistant) {
    try {
      const a: AssistantInitResult = { errors: [] };
      result.assistant = a;
      assistant = assistantPipeline(cfg.assistant, resolveRuntime(cfg.runtime), guards.assistant, a, (widget, error) =>
        result.errors.push({ product: `assistant:${widget}`, error }),
      );
    } catch (error) {
      fail("assistant", "assistant init failed", error);
    }
  }

  let carousels: Array<() => void> = [];
  if (cfg.rce) {
    try {
      const r: RceInitResult = { instances: [], errors: [] };
      result.rce = r;
      carousels = rcePipelines(cfg.rce, ctx, report, r, (index, type, error) =>
        result.errors.push({ product: `rce[${index}:${type}]`, error }),
      );
    } catch (error) {
      fail("rce", "rce init failed", error);
    }
  }

  start("search:dropdown", "search:dropdown", search.dropdown);
  start("search:modal", "search:modal", search.modal);
  start("assistant:modal", "assistant:modal", assistant);
  start("search:results", "search:results", search.results);
  const rceTypes = cfg.rce && Array.isArray(cfg.rce.carousels) ? cfg.rce.carousels : [];
  carousels.forEach((step, index) => {
    const name = `rce[${index}:${String(rceTypes[index]?.type)}]`;
    start(name, name, step);
  });

  // Page-level after-readiness work even when no widget pipeline runs (e.g. an experiment's null arm).
  await domReady;
  await Promise.allSettled(pipelines);
}
